/**
 * Notification Service — Admin API wrapper
 * Phase 4.7.5 (WQ-170)
 */

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

export interface Notification {
  id: string;
  event_type_id: string;
  tenant_id: string | null;
  user_id: string | null;
  title: string;
  body: string | null;
  severity: string;
  category: string;
  entity_type: string | null;
  entity_id: string | null;
  action_type: string | null;
  action_url: string | null;
  action_status: string | null;
  action_completed_by: string | null;
  action_completed_at: string | null;
  is_read: boolean;
  read_at: string | null;
  delivered_channels: string[];
  expires_at: string | null;
  created_at: string;
}

export interface EventType {
  id: string;
  slug: string;
  category: string;
  severity_default: string;
  title_template: string;
  description: string | null;
  producer_service: string;
  action_type: string | null;
  is_actionable: boolean;
  is_active: boolean;
  created_at: string;
}

export interface SubscriptionEntry {
  id: string;
  user_id: string;
  tenant_id: string | null;
  event_type_slug: string;
  channel: string;
  enabled: boolean;
  min_severity: string;
}

export interface SubscriptionsResponse {
  subscriptions: SubscriptionEntry[];
  defaults: Array<{ event_type_slug: string; channel: string; enabled: boolean; min_severity: string }>;
}

const BASE = '/api/admin/notifications';

export async function listNotifications(
  authFetch: FetchFn,
  params?: { category?: string; is_read?: boolean; action_status?: string; limit?: number; offset?: number },
): Promise<Notification[]> {
  const qs = new URLSearchParams();
  if (params?.category) qs.set('category', params.category);
  if (params?.is_read !== undefined) qs.set('is_read', String(params.is_read));
  if (params?.action_status) qs.set('action_status', params.action_status);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const url = qs.toString() ? `${BASE}/?${qs}` : `${BASE}/`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`listNotifications: ${res.status}`);
  return res.json();
}

export async function getUnreadCount(authFetch: FetchFn): Promise<number> {
  try {
    const res = await authFetch(`${BASE}/unread-count`);
    if (!res.ok) return 0;
    const data = await res.json();
    return data.count ?? 0;
  } catch {
    return 0;
  }
}

export async function markRead(authFetch: FetchFn, id: string): Promise<Notification> {
  const res = await authFetch(`${BASE}/${id}/read`, { method: 'PUT' });
  if (!res.ok) throw new Error(`markRead: ${res.status}`);
  return res.json();
}

export async function markAllRead(authFetch: FetchFn, category?: string): Promise<{ updated: number }> {
  const res = await authFetch(`${BASE}/mark-all-read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ category: category || null }),
  });
  if (!res.ok) throw new Error(`markAllRead: ${res.status}`);
  return res.json();
}

export async function dismissNotification(authFetch: FetchFn, id: string): Promise<Notification> {
  const res = await authFetch(`${BASE}/${id}/dismiss`, { method: 'PUT' });
  if (!res.ok) throw new Error(`dismissNotification: ${res.status}`);
  return res.json();
}

export async function completeAction(authFetch: FetchFn, id: string, outcome?: string): Promise<Notification> {
  const res = await authFetch(`${BASE}/${id}/complete`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ outcome: outcome || null }),
  });
  if (!res.ok) throw new Error(`completeAction: ${res.status}`);
  return res.json();
}

export async function getActionQueue(authFetch: FetchFn, limit = 50): Promise<Notification[]> {
  const res = await authFetch(`${BASE}/action-queue?limit=${limit}`);
  if (!res.ok) throw new Error(`getActionQueue: ${res.status}`);
  return res.json();
}

export async function listEventTypes(authFetch: FetchFn): Promise<EventType[]> {
  const res = await authFetch(`${BASE}/event-types`);
  if (!res.ok) throw new Error(`listEventTypes: ${res.status}`);
  return res.json();
}

export async function updateEventType(
  authFetch: FetchFn, slug: string, updates: Partial<Pick<EventType, 'is_active' | 'severity_default'>>,
): Promise<EventType> {
  const res = await authFetch(`${BASE}/event-types/${slug}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`updateEventType: ${res.status}`);
  return res.json();
}

export async function getSubscriptions(authFetch: FetchFn): Promise<SubscriptionsResponse> {
  const res = await authFetch(`${BASE}/subscriptions`);
  if (!res.ok) throw new Error(`getSubscriptions: ${res.status}`);
  return res.json();
}

export async function updateSubscription(
  authFetch: FetchFn,
  data: { event_type_slug: string; channel: string; enabled: boolean; min_severity?: string },
): Promise<SubscriptionEntry> {
  const res = await authFetch(`${BASE}/subscriptions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`updateSubscription: ${res.status}`);
  return res.json();
}
